import { useEffect, type JSX, type ReactNode } from 'react';
import { Navigate, createBrowserRouter, Outlet } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { RoleGate } from '@/components/auth';
import { AuthProvider } from '@/providers/AuthProvider';
import {
  DashboardHome,
  DASHBOARD_ROLES,
  DashboardLeads,
  DashboardRouter,
  DashboardProperties,
  DashboardPropertyCreate,
  DashboardPropertyDetail,
  DashboardShowingAvailability,
  DashboardShowingRequests,
  DashboardAvailabilityAlerts,
  PlaceholderHome,
  LeadDetail,
  PublicAvailabilities,
  PublicPropertyDetail,
  PublicUnitDetail,
  LeadLanding,
  AboutUs,
  ContactUs,
  DashboardPropertyEdit,
  DashboardUnitCreate,
  DashboardUnitEdit,
  SiteSettings,
  PageSettings,
  DashboardTenantProfiles,
} from '@/routes';
import { DashboardUnitDetail } from '@/routes/dashboard/DashboardUnitDetail';
import { DashboardUnitLegacyRedirect } from '@/routes/dashboard/DashboardUnitLegacyRedirect';
import { OutletWithContext } from '@/router/OutletWithContext';

const COLLECTIVE_URL = 'https://tenants.strashin.com/collective';

const withDashboardRoleGate = (children: ReactNode) => (
  <RoleGate allowed={DASHBOARD_ROLES}>{children}</RoleGate>
);

function CollectiveRedirect(): JSX.Element {
  useEffect(() => {
    window.location.replace(COLLECTIVE_URL);
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-cream px-6 py-16 text-center text-text-primary">
      <div className="max-w-md space-y-4">
        <h1 className="text-2xl font-semibold">Opening The Strashin Collective</h1>
        <p className="text-sm text-text-primary/70">
          The Collective now lives on the dedicated tenant site.
        </p>
        <a
          href={COLLECTIVE_URL}
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-brand-accent px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-accent/90"
        >
          Continue to The Collective
        </a>
      </div>
    </main>
  );
}

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <AuthProvider>
          <AppLayout />
        </AuthProvider>
      ),
      children: [
        {
          index: true,
          element: <PlaceholderHome />,
        },
        {
          path: 'about',
          element: <AboutUs />,
        },
        {
          path: 'contact',
          element: <ContactUs />,
        },
        {
          path: 'properties',
          element: <Navigate to="/availabilities" replace />,
        },
        {
          path: 'availabilities',
          element: <PublicAvailabilities />,
        },
        {
          path: 'properties/:propertySlug',
          element: <PublicPropertyDetail />,
        },
        {
          path: 'properties/:propertySlug/units/:unitId',
          element: <PublicUnitDetail />,
        },
        {
          path: 'campaigns/lead',
          element: <LeadLanding />,
        },
        {
          path: 'dashboard',
          element: <DashboardRouter />,
          children: [
            {
              index: true,
              element: <DashboardHome />,
            },
            {
              path: 'leads',
              element: withDashboardRoleGate(<Outlet />),
              children: [
                {
                  index: true,
                  element: <DashboardLeads />,
                },
                {
                  path: ':leadId',
                  element: <LeadDetail />,
                },
              ],
            },
            {
              path: 'showings',
              element: withDashboardRoleGate(<Outlet />),
              children: [
                {
                  index: true,
                  element: <Navigate to="requests" replace />,
                },
                {
                  path: 'availability',
                  element: <DashboardShowingAvailability />,
                },
                {
                  path: 'requests',
                  element: <DashboardShowingRequests />,
                },
              ],
            },
            {
              path: 'properties',
              element: withDashboardRoleGate(<Outlet />),
              children: [
                {
                  index: true,
                  element: <DashboardProperties />,
                },
                {
                  path: 'new',
                  element: <DashboardPropertyCreate />,
                },
                {
                  path: ':propertyId',
                  element: <OutletWithContext />,
                  children: [
                    {
                      index: true,
                      element: <DashboardPropertyDetail />,
                    },
                    {
                      path: 'edit',
                      element: <DashboardPropertyEdit />,
                    },
                    {
                      path: 'units/new',
                      element: <DashboardUnitCreate />,
                    },
                    {
                      path: 'units/:unitId',
                      element: <DashboardUnitDetail />,
                    },
                    {
                      path: 'units/:unitId/edit',
                      element: <DashboardUnitEdit />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'site-settings',
              element: withDashboardRoleGate(<SiteSettings />),
            },
            {
              path: 'tenant-profiles',
              element: withDashboardRoleGate(<DashboardTenantProfiles />),
            },
            {
              path: 'page-settings',
              element: withDashboardRoleGate(<Outlet />),
              children: [
                {
                  index: true,
                  element: <Navigate to="home" replace />,
                },
                {
                  path: ':pageId',
                  element: <PageSettings />,
                },
              ],
            },
            {
              path: 'availability-alerts',
              element: withDashboardRoleGate(<DashboardAvailabilityAlerts />),
            },
            {
              path: 'units/:propertyId/:unitId',
              element: withDashboardRoleGate(<DashboardUnitLegacyRedirect />),
            },
          ],
        },
      ],
    },
    {
      path: '/collective/*',
      element: <CollectiveRedirect />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
